import { useMemo } from 'react';
import { useGetInterests } from 'integration/hooks/useGetInterests';
import { useGetPreferredSizes } from 'integration/hooks/usePreferredSizes';
import { useUserInterests } from 'integration/hooks/useUserInterests';
import {
	DpFanSports,
	DpFanStagesStateSportGroups,
	DpFanTeamData,
	DpFanTeams,
} from 'integration/hooks/useDpFanStages';

export const BRANDS_CODE = 'marcas';
export const ZONA_FAN_CODE = 'zona fan';

export type DpFanNode = {
	id: number;
	parentId: number | null;
	name: string;
	hasImage: boolean;
	children?: DpFanNode[];
};

const normalizeName = (name: string) => name.toLocaleLowerCase().trim();

// Returns the children of the "Zona Fan" top-level node from a preferences tree.
const getZonaFanChildren = (nodes?: DpFanNode[]): DpFanNode[] => {
	const zonaFan = (nodes ?? []).find((item) => normalizeName(item.name) === ZONA_FAN_CODE);
	return zonaFan?.children ?? [];
};

export const flattenLeaves = (node: { children?: DpFanNode[]; id: number; name: string }): DpFanTeamData[] => {
	const children = node.children ?? [];

	return children.flatMap((child) => {
		const hasGrandChildren = (child.children?.length ?? 0) > 0;

		if (hasGrandChildren) {
			return flattenLeaves(child);
		}

		return [
			{
				id: child.id,
				parentId: node.id,
				parentName: node.name,
				name: child.name,
				hasImage: child.hasImage,
			},
		];
	});
};

export const useDpFanFetcher = () => {
	const preferredSizesQuery = useGetPreferredSizes();
	const interestsListQuery = useGetInterests();
	const userInterestsQuery = useUserInterests();

	const zonaFanChildren = useMemo(
		() => getZonaFanChildren(interestsListQuery.data as unknown as DpFanNode[] | undefined),
		[interestsListQuery.data]
	);

	const userZonaFanChildren = useMemo(
		() => getZonaFanChildren(userInterestsQuery.userInterests as unknown as DpFanNode[] | undefined),
		[userInterestsQuery.userInterests]
	);

	// Selectable categories = every Zona Fan child except the "Marcas" node.
	const interests = useMemo(
		() => zonaFanChildren.filter((item) => normalizeName(item.name) !== BRANDS_CODE),
		[zonaFanChildren]
	);

	const brandsListData = useMemo(
		() => zonaFanChildren.find((item) => normalizeName(item.name) === BRANDS_CODE),
		[zonaFanChildren]
	);

	const userSportsData = useMemo(
		() => userZonaFanChildren.filter((item) => normalizeName(item.name) !== BRANDS_CODE),
		[userZonaFanChildren]
	);

	const userBrandsData = useMemo(
		() => userZonaFanChildren.find((item) => normalizeName(item.name) === BRANDS_CODE),
		[userZonaFanChildren]
	);

	const sports = useMemo<DpFanSports>(
		() =>
			interests.map((item) => ({
				id: item.id,
				name: item.name,
				parentId: item.parentId,
				hasImage: item.hasImage,
			})),
		[interests]
	);

	const teams = useMemo<DpFanTeams>(
		() =>
			interests.reduce((acc, interest) => {
				acc[interest.name] = flattenLeaves(interest);
				return acc;
			}, {} as DpFanTeams),
		[interests]
	);

	const brands = useMemo<DpFanSports>(
		() =>
			brandsListData?.children?.map((item) => ({
				id: item.id,
				name: item.name,
				parentId: item.parentId,
				hasImage: item.hasImage,
			})) || [],
		[brandsListData]
	);
	const sportGroups = useMemo<DpFanStagesStateSportGroups>(
		() =>
			Object.entries(teams).reduce((acc, [sport, items]) => {
				acc[sport] = items.reduce((groups, item) => {
					if (!groups.includes(item.parentName)) {
						groups.push(item.parentName);
					}
					return groups;
				}, [] as string[]);
				return acc;
			}, {} as DpFanStagesStateSportGroups),
		[teams]
	);

	return {
		userBrandsData,
		interests,
		sports,
		teams,
		brands,
		sportGroups,
		userSportsData,
		preferredSizes: preferredSizesQuery.data || {},
		isLoadingPreferredSizes: preferredSizesQuery.isLoading,
		isLoadingInterests: interestsListQuery.isLoading,
		isLoadingUserInterests: userInterestsQuery.isLoadingUserInterests,
		isUserLoggedIn: userInterestsQuery.isUserLoggedIn,
		onUpdateUserInterests: userInterestsQuery.onUpdateUserInterests,
	};
};
